import { useEffect, useState, useCallback } from "react";
import api from "../api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Field } from "./FormField";
import { eur } from "../helpers";
import { Plus, Paperclip, Pencil, Trash2, FileDown, Upload, X, Repeat } from "lucide-react";

const MONTHS = ["Gen", "Feb", "Mar", "Apr", "Mag", "Giu", "Lug", "Ago", "Set", "Ott", "Nov", "Dic"];
const now = new Date();
const empty = (aptId) => ({
  apartment_id: aptId || "", date: new Date().toISOString().slice(0, 10), category: "Altro",
  supplier: "", description: "", amount_net: 0, vat_percentage: 22, paid_status: "da_pagare",
  payment_date: "", payment_method: "", invoice_number: "", attachment_url: "", attachment_name: "",
  recurring_type: "nessuna", recurring_until: "", notes: "",
});

export default function ExpenseManager({ apartmentId = null, onChanged }) {
  const [meta, setMeta] = useState({ categories: [], payment_methods: [], recurring_types: [] });
  const [apts, setApts] = useState([]);
  const [rows, setRows] = useState([]);
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(0); // 0 = all months
  const [fCat, setFCat] = useState("all");
  const [fPaid, setFPaid] = useState("all");
  const [fSupplier, setFSupplier] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty(apartmentId));
  const [uploading, setUploading] = useState(false);

  useEffect(() => { api.get("/apartment-expenses/meta").then((r) => setMeta(r.data)); }, []);
  useEffect(() => { if (!apartmentId) api.get("/apartments/lookup").then((r) => setApts(r.data)); }, [apartmentId]);

  const load = useCallback(() => {
    const params = { year };
    if (month) params.month = month;
    if (apartmentId) params.apartment_id = apartmentId;
    if (fCat !== "all") params.category = fCat;
    if (fPaid !== "all") params.paid_status = fPaid;
    if (fSupplier.trim()) params.supplier = fSupplier.trim();
    api.get("/apartment-expenses", { params }).then((r) => setRows(r.data)).catch(() => setRows([]));
  }, [year, month, apartmentId, fCat, fPaid, fSupplier]);
  useEffect(() => { load(); }, [load]);

  const aptName = (id) => apts.find((a) => a.id === id)?.name || (apartmentId ? "" : id);
  const vatAmt = (form.amount_net || 0) * (form.vat_percentage || 0) / 100;
  const grossAmt = (form.amount_net || 0) + vatAmt;

  const openNew = () => { setEditing(null); setForm(empty(apartmentId)); setOpen(true); };
  const openEdit = (r) => { setEditing(r.id); setForm({ ...empty(apartmentId), ...r }); setOpen(true); };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, attachment_url: res.data.url, attachment_name: file.name }));
      toast.success("Allegato caricato");
    } catch { toast.error("Errore upload allegato"); }
    setUploading(false);
  };

  const save = async () => {
    if (!form.apartment_id) { toast.error("Seleziona un appartamento"); return; }
    if (!form.date) { toast.error("Inserisci la data"); return; }
    const payload = { ...form, amount_net: +(form.amount_net || 0), vat_percentage: +(form.vat_percentage || 0) };
    try {
      if (editing) await api.put(`/apartment-expenses/${editing}`, payload);
      else await api.post("/apartment-expenses", payload);
      toast.success(editing ? "Costo aggiornato" : "Costo aggiunto");
      setOpen(false); load(); onChanged?.();
    } catch { toast.error("Errore salvataggio costo"); }
  };

  const del = async (r) => {
    if (!window.confirm(`Eliminare il costo "${r.category} · ${r.date}"?`)) return;
    try { await api.delete(`/apartment-expenses/${r.id}`); toast.success("Costo eliminato"); load(); onChanged?.(); }
    catch { toast.error("Errore eliminazione"); }
  };

  const exportCsv = async () => {
    try {
      const params = { year };
      if (month) params.month = month;
      if (apartmentId) params.apartment_id = apartmentId;
      if (fCat !== "all") params.category = fCat;
      if (fPaid !== "all") params.paid_status = fPaid;
      if (fSupplier.trim()) params.supplier = fSupplier.trim();
      const res = await api.get("/apartment-expenses/csv", { params, responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url; a.download = "costi_appartamenti.csv";
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch { toast.error("Errore export"); }
  };

  const totNet = rows.reduce((s, r) => s + (r.amount_net || 0), 0);
  const totVat = rows.reduce((s, r) => s + (r.amount_vat || 0), 0);
  const totGross = rows.reduce((s, r) => s + (r.amount_gross || 0), 0);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  return (
    <div className="space-y-4" data-testid="expense-manager">
      {/* Toolbar */}
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div className="flex flex-wrap items-end gap-2">
          <Field label="Anno">
            <Select value={String(year)} onValueChange={(v) => setYear(+v)}>
              <SelectTrigger data-testid="exp-filter-year" className="w-24"><SelectValue /></SelectTrigger>
              <SelectContent>{[now.getFullYear(), now.getFullYear() - 1, now.getFullYear() - 2].map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <Field label="Mese">
            <Select value={String(month)} onValueChange={(v) => setMonth(+v)}>
              <SelectTrigger data-testid="exp-filter-month" className="w-28"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="0">Tutti</SelectItem>{MONTHS.map((m, i) => <SelectItem key={i} value={String(i + 1)}>{m}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <Field label="Categoria">
            <Select value={fCat} onValueChange={setFCat}>
              <SelectTrigger data-testid="exp-filter-category" className="w-36"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="all">Tutte</SelectItem>{meta.categories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <Field label="Stato">
            <Select value={fPaid} onValueChange={setFPaid}>
              <SelectTrigger data-testid="exp-filter-paid" className="w-32"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="all">Tutti</SelectItem><SelectItem value="pagato">Pagato</SelectItem><SelectItem value="da_pagare">Da pagare</SelectItem></SelectContent>
            </Select>
          </Field>
          <Field label="Fornitore">
            <Input data-testid="exp-filter-supplier" className="w-40" placeholder="Cerca fornitore" value={fSupplier} onChange={(e) => setFSupplier(e.target.value)} />
          </Field>
        </div>
        <div className="flex gap-2">
          <Button data-testid="exp-export-csv" variant="outline" onClick={exportCsv} className="gap-2"><FileDown size={16} /> CSV</Button>
          <Button data-testid="exp-new-btn" onClick={openNew} className="gap-2 bg-primary hover:bg-primary/90"><Plus size={16} /> Nuovo costo</Button>
        </div>
      </div>

      {/* Totals */}
      <div className="grid grid-cols-3 gap-3">
        <Card className="border-slate-200 p-3"><div className="text-xs text-slate-500">Imponibile</div><div className="font-head text-lg font-semibold text-slate-900">{eur(totNet)}</div></Card>
        <Card className="border-slate-200 p-3"><div className="text-xs text-slate-500">IVA</div><div className="font-head text-lg font-semibold text-slate-900">{eur(totVat)}</div></Card>
        <Card className="border-slate-200 p-3"><div className="text-xs text-slate-500">Totale</div><div className="font-head text-lg font-semibold text-primary">{eur(totGross)}</div></Card>
      </div>

      {/* List */}
      <Card className="overflow-hidden border-slate-200">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="exp-table">
            <thead className="border-b border-slate-200 bg-slate-50 text-left text-xs uppercase text-slate-500">
              <tr>
                <th className="px-3 py-3">Data</th>
                {!apartmentId && <th className="px-3 py-3">Appartamento</th>}
                <th className="px-3 py-3">Categoria</th>
                <th className="px-3 py-3">Fornitore</th>
                <th className="px-3 py-3">N. Fattura</th>
                <th className="px-3 py-3 text-right">Imponibile</th>
                <th className="px-3 py-3 text-right">IVA</th>
                <th className="px-3 py-3 text-right">Totale</th>
                <th className="px-3 py-3">Stato</th>
                <th className="px-3 py-3"></th>
                <th className="px-3 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} data-testid={`exp-row-${r.id}`} className="border-b border-slate-100 hover:bg-slate-50/60">
                  <td className="px-3 py-2.5 text-slate-700">{r.date}</td>
                  {!apartmentId && <td className="px-3 py-2.5 text-slate-600">{aptName(r.apartment_id)}</td>}
                  <td className="px-3 py-2.5">
                    <span className="inline-flex items-center gap-1 font-medium text-slate-900">
                      {r.category}{r.recurring_type && r.recurring_type !== "nessuna" && <Repeat size={12} className="text-violet-500" title={r.recurring_type} />}
                    </span>
                    {r.description && <div className="text-xs text-slate-400">{r.description}</div>}
                  </td>
                  <td className="px-3 py-2.5 text-slate-600">{r.supplier || "—"}</td>
                  <td className="px-3 py-2.5 text-slate-500">{r.invoice_number || "—"}</td>
                  <td className="px-3 py-2.5 text-right text-slate-700">{eur(r.amount_net)}</td>
                  <td className="px-3 py-2.5 text-right text-slate-500">{eur(r.amount_vat)} <span className="text-xs text-slate-400">({r.vat_percentage}%)</span></td>
                  <td className="px-3 py-2.5 text-right font-semibold text-slate-900">{eur(r.amount_gross)}</td>
                  <td className="px-3 py-2.5">
                    <Badge className={r.paid_status === "pagato" ? "bg-emerald-100 text-emerald-700" : "bg-rose-100 text-rose-700"}>
                      {r.paid_status === "pagato" ? "Pagato" : "Da pagare"}
                    </Badge>
                  </td>
                  <td className="px-3 py-2.5">
                    {r.attachment_url ? (
                      <a data-testid={`exp-attach-${r.id}`} href={`${api.defaults.baseURL}${r.attachment_url.replace("/api", "")}`} target="_blank" rel="noreferrer" className="text-primary hover:text-primary/70" title={r.attachment_name}>
                        <Paperclip size={16} />
                      </a>
                    ) : <span className="text-slate-300"><Paperclip size={16} /></span>}
                  </td>
                  <td className="px-3 py-2.5">
                    <div className="flex gap-1">
                      <button data-testid={`exp-edit-${r.id}`} onClick={() => openEdit(r)} className="rounded p-1.5 text-slate-500 hover:bg-slate-100 hover:text-primary"><Pencil size={15} /></button>
                      <button data-testid={`exp-del-${r.id}`} onClick={() => del(r)} className="rounded p-1.5 text-slate-500 hover:bg-rose-50 hover:text-rose-600"><Trash2 size={15} /></button>
                    </div>
                  </td>
                </tr>
              ))}
              {rows.length === 0 && <tr><td colSpan={apartmentId ? 10 : 11} className="px-4 py-10 text-center text-slate-400">Nessun costo per il periodo selezionato</td></tr>}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Modal */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl" data-testid="exp-dialog">
          <DialogHeader><DialogTitle>{editing ? "Modifica costo" : "Nuovo costo"}</DialogTitle></DialogHeader>
          <div className="space-y-5">
            {/* Dati costo */}
            <div>
              <h4 className="mb-2 text-xs font-semibold uppercase text-slate-400">Dati costo</h4>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                {!apartmentId && (
                  <Field label="Appartamento *">
                    <Select value={form.apartment_id} onValueChange={(v) => set("apartment_id", v)}>
                      <SelectTrigger data-testid="exp-apartment"><SelectValue placeholder="Seleziona" /></SelectTrigger>
                      <SelectContent>{apts.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}</SelectContent>
                    </Select>
                  </Field>
                )}
                <Field label="Data *"><Input data-testid="exp-date" type="date" value={form.date} onChange={(e) => set("date", e.target.value)} /></Field>
                <Field label="Categoria">
                  <Select value={form.category} onValueChange={(v) => set("category", v)}>
                    <SelectTrigger data-testid="exp-category"><SelectValue /></SelectTrigger>
                    <SelectContent>{meta.categories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                  </Select>
                </Field>
                <Field label="Fornitore"><Input data-testid="exp-supplier" value={form.supplier} onChange={(e) => set("supplier", e.target.value)} placeholder="es. Enel Energia" /></Field>
                <Field label="Descrizione" className="sm:col-span-2"><Input data-testid="exp-description" value={form.description} onChange={(e) => set("description", e.target.value)} placeholder="es. Bolletta luce bimestre" /></Field>
              </div>
            </div>

            {/* Importi */}
            <div>
              <h4 className="mb-2 text-xs font-semibold uppercase text-slate-400">Importi</h4>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-4">
                <Field label="Imponibile (€)"><Input data-testid="exp-net" type="number" step="0.01" value={form.amount_net} onChange={(e) => set("amount_net", +e.target.value)} /></Field>
                <Field label="IVA %">
                  <Select value={String(form.vat_percentage)} onValueChange={(v) => set("vat_percentage", +v)}>
                    <SelectTrigger data-testid="exp-vat"><SelectValue /></SelectTrigger>
                    <SelectContent>{[0, 4, 10, 22].map((v) => <SelectItem key={v} value={String(v)}>{v}%</SelectItem>)}</SelectContent>
                  </Select>
                </Field>
                <Field label="IVA (€)"><Input value={vatAmt.toFixed(2)} disabled className="bg-slate-50" data-testid="exp-vat-amt" /></Field>
                <Field label="Totale (€)"><Input value={grossAmt.toFixed(2)} disabled className="bg-slate-50 font-semibold" data-testid="exp-gross" /></Field>
              </div>
            </div>

            {/* Pagamento */}
            <div>
              <h4 className="mb-2 text-xs font-semibold uppercase text-slate-400">Pagamento</h4>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <Field label="Stato">
                  <Select value={form.paid_status} onValueChange={(v) => set("paid_status", v)}>
                    <SelectTrigger data-testid="exp-paid"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="da_pagare">Da pagare</SelectItem><SelectItem value="pagato">Pagato</SelectItem></SelectContent>
                  </Select>
                </Field>
                <Field label="Metodo di pagamento">
                  <Select value={form.payment_method || "none"} onValueChange={(v) => set("payment_method", v === "none" ? "" : v)}>
                    <SelectTrigger data-testid="exp-method"><SelectValue placeholder="—" /></SelectTrigger>
                    <SelectContent><SelectItem value="none">—</SelectItem>{meta.payment_methods.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
                  </Select>
                </Field>
                {form.paid_status === "pagato" && <Field label="Data pagamento"><Input data-testid="exp-paydate" type="date" value={form.payment_date} onChange={(e) => set("payment_date", e.target.value)} /></Field>}
                <Field label="N. Fattura / Documento"><Input data-testid="exp-invoice" value={form.invoice_number} onChange={(e) => set("invoice_number", e.target.value)} placeholder="es. 2026/123" /></Field>
              </div>
            </div>

            {/* Allegato */}
            <div>
              <h4 className="mb-2 text-xs font-semibold uppercase text-slate-400">Allegato (PDF / immagine)</h4>
              {form.attachment_url ? (
                <div className="flex items-center justify-between rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
                  <span className="flex items-center gap-2 text-sm text-slate-700"><Paperclip size={15} /> {form.attachment_name || "Allegato"}</span>
                  <button data-testid="exp-attach-remove" onClick={() => set("attachment_url", "") || set("attachment_name", "")} className="text-slate-400 hover:text-rose-600"><X size={16} /></button>
                </div>
              ) : (
                <label className="flex cursor-pointer items-center gap-2 rounded-lg border border-dashed border-slate-300 px-3 py-3 text-sm text-slate-500 hover:border-primary hover:text-primary" data-testid="exp-attach-label">
                  <Upload size={16} /> {uploading ? "Caricamento…" : "Carica file (PDF, JPG, PNG)"}
                  <input type="file" accept=".pdf,.jpg,.jpeg,.png,.webp" className="hidden" onChange={handleUpload} data-testid="exp-attach-input" />
                </label>
              )}
            </div>

            {/* Ricorrenza + Note */}
            <div>
              <h4 className="mb-2 text-xs font-semibold uppercase text-slate-400">Ricorrenza & note</h4>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <Field label="Ricorrenza">
                  <Select value={form.recurring_type} onValueChange={(v) => set("recurring_type", v)}>
                    <SelectTrigger data-testid="exp-recurring"><SelectValue /></SelectTrigger>
                    <SelectContent>{meta.recurring_types.map((r) => <SelectItem key={r} value={r}>{r === "nessuna" ? "Nessuna" : r.charAt(0).toUpperCase() + r.slice(1)}</SelectItem>)}</SelectContent>
                  </Select>
                </Field>
                {form.recurring_type !== "nessuna" && <Field label="Ricorrente fino a"><Input data-testid="exp-recurring-until" type="date" value={form.recurring_until} onChange={(e) => set("recurring_until", e.target.value)} /></Field>}
                <Field label="Note" className="sm:col-span-2"><Textarea data-testid="exp-notes" rows={2} value={form.notes} onChange={(e) => set("notes", e.target.value)} /></Field>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} data-testid="exp-cancel">Annulla</Button>
            <Button onClick={save} className="bg-primary hover:bg-primary/90" data-testid="exp-save">{editing ? "Salva modifiche" : "Aggiungi costo"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
